import React, { useState, useEffect, useContext } from 'react';
import {
    Container,
    Typography,
    Button,
    Box,
    Card,
    CardContent,
    CircularProgress,
    Divider
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Dashboard as DashboardIcon,
    Download as DownloadIcon
} from '@mui/icons-material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { userContext } from '../../stores/users/userContext';
import { messageContext } from '../../stores/messages/messageContext';
import { hostApi } from '../constants';

const SubscriptionSuccess = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const userStore = useContext(userContext);
    const messageStore = useContext(messageContext);
    const [loading, setLoading] = useState(true);
    const [subscriptionData, setSubscriptionData] = useState(null);

    const rawSessionId = searchParams.get('session_id');
    // Clean session ID by removing any wrapping braces
    const sessionId = rawSessionId ? rawSessionId.replace(/^{|}$/g, '') : null;

    useEffect(() => {
        // Debug: Log URL and parameters
        console.log('=== SUBSCRIPTION SUCCESS DEBUG ===');
        console.log('Current URL:', window.location.href);
        console.log('Search params toString():', searchParams.toString());
        console.log('Raw session ID from params:', rawSessionId);
        console.log('Cleaned session ID:', sessionId);
        console.log('All URL search params:', Array.from(searchParams.entries()));
        console.log('Window location search:', window.location.search);
        console.log('Window location pathname:', window.location.pathname);
        console.log('===================================');

        const verifyPayment = async () => {
            if (!sessionId) {
                console.error('❌ No session ID found!');
                console.log('All params available:', Array.from(searchParams.entries()));
                messageStore.setMessage('No session ID found in URL', 'error');
                setLoading(false);
                return;
            }

            // No more demo mode - using real backend API

            try {
                const formData = new FormData();
                formData.append('token', userStore.getToken());
                formData.append('session_id', sessionId);

                console.log('Sending session_id to backend:', sessionId);
                console.log('Session ID length:', sessionId.length);

                const response = await fetch(`${hostApi}verify-payment/`, {
                    method: 'POST',
                    body: formData,
                });

                const data = await response.json();

                if (response.ok) {
                    setSubscriptionData(data);
                    messageStore.setMessage('Subscription activated successfully!', 'success');
                    // Refresh user info to get updated subscription status
                    if (userStore.isUserAuth()) {
                        await userStore.getUserInfo();
                    }
                } else {
                    messageStore.setMessage(data.error || 'Failed to verify payment', 'error');
                }
            } catch (err) {
                console.error('Payment verification error:', err);
                messageStore.setMessage('Failed to verify payment. Please contact support.', 'error');
            } finally {
                setLoading(false);
            }
        };

        verifyPayment();
    }, [sessionId, userStore, messageStore]);

    const handleGoToDashboard = () => {
        navigate('/downloaded/');
    };

    const handleViewPlans = () => {
        navigate('/subscription/');
    };

    if (loading) {
        return (
            <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
                <CircularProgress size={60} />
                <Typography variant="h6" sx={{ mt: 2 }}>
                    Verifying your payment...
                </Typography>
            </Container>
        );
    }

    if (!subscriptionData) {
        return (
            <Container maxWidth="sm" sx={{ py: 8 }}>
                <Box textAlign="center">
                    <Typography variant="h4" gutterBottom>
                        Payment Verification Failed
                    </Typography>
                    <Typography variant="body1" paragraph>
                        We couldn't verify your payment. Please check your email for confirmation
                        or contact support if the issue persists.
                    </Typography>
                    <Button variant="contained" onClick={handleViewPlans}>
                        Back to Plans
                    </Button>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="sm" sx={{ py: 8 }}>
            <Card sx={{ textAlign: 'center', p: 4 }}>
                <CardContent>
                    <CheckCircleIcon
                        sx={{
                            fontSize: 80,
                            color: 'success.main',
                            mb: 2
                        }}
                    />

                    <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
                        Welcome to {subscriptionData?.plan_name || 'Pro'}!
                    </Typography>

                    <Typography variant="h6" color="text.secondary" paragraph>
                        Your subscription has been activated successfully
                    </Typography>

                    {subscriptionData && (
                        <Box sx={{ my: 4 }}>
                            <Divider sx={{ my: 2 }} />

                            <Typography variant="body1" paragraph>
                                <strong>Plan:</strong> {subscriptionData.plan_name}
                            </Typography>

                            <Typography variant="body1" paragraph>
                                <strong>Status:</strong> {subscriptionData.status}
                            </Typography>

                            {subscriptionData.trial_end && (
                                <Typography variant="body1" paragraph>
                                    <strong>Trial ends:</strong> {new Date(subscriptionData.trial_end).toLocaleDateString()}
                                </Typography>
                            )}

                            {subscriptionData.next_billing && (
                                <Typography variant="body1" paragraph>
                                    <strong>Next billing:</strong> {new Date(subscriptionData.next_billing).toLocaleDateString()}
                                </Typography>
                            )}

                            <Divider sx={{ my: 2 }} />
                        </Box>
                    )}

                    <Typography variant="body1" paragraph sx={{ mb: 4 }}>
                        You now have access to all premium features including unlimited transcriptions,
                        advanced export options, and priority support.
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<DashboardIcon />}
                            onClick={handleGoToDashboard}
                            sx={{ py: 1.5 }}
                        >
                            Go to Dashboard
                        </Button>

                        <Button
                            variant="outlined"
                            size="large"
                            startIcon={<DownloadIcon />}
                            href="/upload-video/"
                            sx={{ py: 1.5 }}
                        >
                            Start Transcribing
                        </Button>
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
                        Need help getting started? Check out our{' '}
                        <Button variant="text" size="small">
                            documentation
                        </Button>{' '}
                        or{' '}
                        <Button variant="text" size="small" href="/contact/">
                            contact support
                        </Button>
                    </Typography>
                </CardContent>
            </Card>
        </Container>
    );
};

export default SubscriptionSuccess; 